import { useCallback, useEffect, useState } from "react";
import { api, type AgentOsHistoryRunView, type AgentOsMonitoringView, type AgentOsReplayView, type AgentOsRuleView, type AgentOsRunCreateResponse, type AgentOsRunEventView, type AgentOsSettingsView } from "../api";
import { useRef } from "react";
import { AgentOsMissionConsolePanel } from "../dashboard/AgentOsMissionConsolePanel";
import { ChatView } from "./ChatView";
import { ownerFacingGlobalError } from "../ownerFacingGlobalError";

type AgentOsMissionErrorContext =
  | { action: "settings" | "rules" | "history" | "monitoring" | "createRun" }
  | { action: "replay" | "events"; runId: string };

interface AgentOsMissionTaggedError {
  __agentOsMissionConsoleContext: AgentOsMissionErrorContext;
  cause: unknown;
}

function tagAgentOsError<T>(promise: Promise<T>, context: AgentOsMissionErrorContext): Promise<T> {
  return promise.catch((cause) => {
    throw { __agentOsMissionConsoleContext: context, cause } satisfies AgentOsMissionTaggedError;
  });
}

function isTaggedAgentOsError(err: unknown): err is AgentOsMissionTaggedError {
  return typeof err === "object" && err !== null && "__agentOsMissionConsoleContext" in err && "cause" in err;
}

function agentOsEndpoint(context: AgentOsMissionErrorContext): string {
  switch (context.action) {
    case "settings":
      return "/api/agent-os/settings";
    case "rules":
      return "/api/agent-os/rules";
    case "history":
      return "/api/agent-os/history";
    case "monitoring":
      return "/api/agent-os/monitoring";
    case "createRun":
      return "/api/agent-os/runs";
    case "replay":
      return `/api/agent-os/runs/${encodeURIComponent(context.runId)}/replay`;
    case "events":
      return `/api/agent-os/runs/${encodeURIComponent(context.runId)}/events`;
  }
}

function agentOsActionLabel(context: AgentOsMissionErrorContext): string {
  switch (context.action) {
    case "settings":
      return "Agent-OS-Einstellungen konnten nicht geladen werden";
    case "rules":
      return "Agent-OS-Regeln konnten nicht geladen werden";
    case "history":
      return "Agent-OS-Historie konnte nicht geladen werden";
    case "monitoring":
      return "Agent-OS-Monitoring konnte nicht geladen werden";
    case "createRun":
      return "Agent-OS-Run konnte nicht gestartet werden";
    case "replay":
      return "Agent-OS-Replay konnte nicht geladen werden";
    case "events":
      return "Agent-OS-Live-Events konnten nicht geladen werden";
  }
}

function agentOsIssueCopy(err: unknown, endpoint: string): string {
  const text = err instanceof Error ? err.message : String(err);
  if (/^(Owner-Session|Owner Auth|API-Zugriff|Live-Stream)/.test(text) && /Endpoint:/.test(text)) {
    return ownerFacingGlobalError(text);
  }
  const requestId = text.match(/Request-ID\s+([A-Za-z0-9._:-]+)/i)?.[1];
  const requestSuffix = requestId ? ` Request-ID ${requestId}` : "";
  if (/invalid_grant|token endpoint|expired or revoked|has been expired or revoked/i.test(text)) {
    return ownerFacingGlobalError(text);
  }
  if (/401|missing_session|invalid_session|Owner-Session|Owner Auth/i.test(text)) {
    return `Owner-Session fehlt oder ist abgelaufen. Bitte neu anmelden und danach Agent-OS-Daten erneut laden. Endpoint: ${endpoint}.${requestSuffix}`;
  }
  if (/unauthorized|Bearer|Authorization|API-Zugriff/i.test(text)) {
    return `API-Zugriff nicht freigegeben. Der Server hat den Request vor der Owner-Session-Pruefung abgelehnt. Endpoint: ${endpoint}.${requestSuffix}`;
  }
  if (/403|forbidden|insufficient_role|permission_denied/i.test(text)) {
    return `Owner-Freigabe erforderlich. Deine Session ist vorhanden, aber diese Agent-OS-Aktion braucht Owner/Admin-Rechte. Endpoint: ${endpoint}.${requestSuffix}`;
  }
  if (/^\s*[\[{]|error_description|traceback|stack trace|fetch failed|networkerror|econnreset|enotfound|etimedout|timeout|exception/i.test(text)) {
    return "Agent-OS-Aktion konnte technisch nicht belastbar abgeschlossen werden. Technische Details bleiben serverseitige Evidence; bitte Status erneut pruefen.";
  }
  return ownerFacingGlobalError(text);
}

export function agentOsMissionConsoleErrorCopy(err: unknown, fallbackContext: AgentOsMissionErrorContext): string {
  const context = isTaggedAgentOsError(err) ? err.__agentOsMissionConsoleContext : fallbackContext;
  const cause = isTaggedAgentOsError(err) ? err.cause : err;
  const endpoint = agentOsEndpoint(context);
  return `${agentOsActionLabel(context)}. Endpoint: ${endpoint}. ${agentOsIssueCopy(cause, endpoint)}`;
}

export function SimoTAgentOsMissionConsoleView(): JSX.Element {
  const [settings, setSettings] = useState<AgentOsSettingsView | null>(null);
  const [rules, setRules] = useState<AgentOsRuleView[]>([]);
  const [lastRun, setLastRun] = useState<AgentOsRunCreateResponse | null>(null);
  const [history, setHistory] = useState<AgentOsHistoryRunView[]>([]);
  const [historyPagination, setHistoryPagination] = useState<{ limit: number; offset: number; total: number; hasMore: boolean } | null>(null);
  const [selectedReplay, setSelectedReplay] = useState<AgentOsReplayView | null>(null);
  const [liveEvents, setLiveEvents] = useState<AgentOsRunEventView[]>([]);
  const [monitoring, setMonitoring] = useState<AgentOsMonitoringView | null>(null);
  const [historyQuery, setHistoryQuery] = useState("");
  const [historyStatus, setHistoryStatus] = useState<"all" | AgentOsHistoryRunView["status"]>("done");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const eventsAbortRef = useRef<AbortController | null>(null);

  const loadRunEvents = useCallback(async (runId: string, seed: AgentOsRunEventView[] = []): Promise<void> => {
    eventsAbortRef.current?.abort();
    const abort = new AbortController();
    eventsAbortRef.current = abort;
    setLiveEvents(seed);
    try {
      for await (const event of api.agentOsRunEvents(runId, abort.signal)) {
        setLiveEvents((current) => current.some((existing) => existing.at === event.at && existing.type === event.type && existing.summary === event.summary)
          ? current
          : [...current, event].slice(-80));
      }
    } catch (err) {
      if (!abort.signal.aborted) {
        setLiveEvents((current) => [...current, { type: "error", summary: agentOsMissionConsoleErrorCopy(err, { action: "events", runId }), at: new Date().toISOString() }].slice(-80));
      }
    }
  }, []);

  const refresh = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const [settingsRes, rulesRes, historyRes, monitoringRes] = await Promise.all([
        tagAgentOsError(api.agentOsSettings(), { action: "settings" }),
        tagAgentOsError(api.agentOsRules(), { action: "rules" }),
        tagAgentOsError(api.agentOsHistory({
          q: historyQuery.trim() || undefined,
          status: historyStatus === "all" ? undefined : historyStatus,
          limit: 100,
        }), { action: "history" }),
        tagAgentOsError(api.agentOsMonitoring(), { action: "monitoring" }),
      ]);
      setSettings(settingsRes.settings);
      setRules(rulesRes.rules);
      setHistory(historyRes.runs);
      setHistoryPagination(historyRes.pagination ?? null);
      setMonitoring(monitoringRes.monitoring);
    } catch (err) {
      setError(agentOsMissionConsoleErrorCopy(err, { action: "settings" }));
    } finally {
      setLoading(false);
    }
  }, [historyQuery, historyStatus]);

  const loadMoreHistory = useCallback(async (): Promise<void> => {
    if (!historyPagination?.hasMore) return;
    setError(null);
    try {
      const res = await api.agentOsHistory({
        q: historyQuery.trim() || undefined,
        status: historyStatus === "all" ? undefined : historyStatus,
        limit: historyPagination.limit,
        offset: history.length,
      });
      setHistory((current) => [...current, ...res.runs.filter((run) => !current.some((existing) => existing.id === run.id))]);
      setHistoryPagination(res.pagination ?? null);
    } catch (err) {
      setError(agentOsMissionConsoleErrorCopy(err, { action: "history" }));
    }
  }, [history.length, historyPagination, historyQuery, historyStatus]);

  const selectRun = useCallback(async (runId: string): Promise<void> => {
    setError(null);
    try {
      const res = await api.agentOsReplay(runId);
      setSelectedReplay(res.replay);
      void loadRunEvents(runId);
    } catch (err) {
      setError(agentOsMissionConsoleErrorCopy(err, { action: "replay", runId }));
    }
  }, [loadRunEvents]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  useEffect(() => () => {
    eventsAbortRef.current?.abort();
  }, []);

  useEffect(() => {
    const selectedStillVisible = selectedReplay && history.some((run) => run.id === selectedReplay.run.id);
    if (selectedStillVisible) return;
    if (history[0]) {
      void selectRun(history[0].id);
      return;
    }
    if (selectedReplay) setSelectedReplay(null);
  }, [history, selectedReplay, selectRun]);

  const updateMode = async (mode: AgentOsSettingsView["activeMode"]): Promise<void> => {
    setError(null);
    try {
      const res = await api.updateAgentOsSettings({ activeMode: mode });
      setSettings(res.settings);
    } catch (err) {
      setError(agentOsMissionConsoleErrorCopy(err, { action: "settings" }));
    }
  };

  const updateSettings = async (patch: Partial<Pick<AgentOsSettingsView, "clarificationPolicy" | "reads" | "changes" | "modelDefaults">>): Promise<void> => {
    setError(null);
    try {
      const res = await api.updateAgentOsSettings(patch);
      setSettings(res.settings);
    } catch (err) {
      setError(agentOsMissionConsoleErrorCopy(err, { action: "settings" }));
    }
  };

  const createRun = async (expectedAction: "read" | "write"): Promise<void> => {
    setError(null);
    try {
      const res = await api.createAgentOsRun({
        prompt: expectedAction === "read"
          ? "SimoT Agent OS: sammle Repo-Kontext, Regeln und naechste sichere Schritte."
          : "SimoT Agent OS: plane eine sichere Aenderung mit Diff-, Approval- und Evidence-Gate.",
        mode: settings?.activeMode ?? "autonomous_gated",
        expectedAction,
        startPolicy: expectedAction === "read" ? "run_read" : "run_gated",
      });
      setLastRun(res);
      void loadRunEvents(res.run.id, res.eventsSummary);
      await refresh();
    } catch (err) {
      setError(agentOsMissionConsoleErrorCopy(err, { action: "createRun" }));
    }
  };

  return (
    <div className="h-full min-h-0 overflow-y-auto simo-thin-scroll simo-grid-bg p-3 sm:p-4">
      <div className="grid min-h-full gap-4 xl:grid-cols-[minmax(0,1fr)_minmax(500px,0.65fr)]">
        <div className="min-w-0 space-y-4">
          <AgentOsMissionConsolePanel
            settings={settings}
            rules={rules}
            lastRun={lastRun ? { ...lastRun.run, executionPolicy: lastRun.executionPolicy } : null}
            history={history}
            historyPagination={historyPagination}
            selectedReplay={selectedReplay}
            liveEvents={liveEvents}
            monitoring={monitoring}
            historyQuery={historyQuery}
            historyStatus={historyStatus}
            error={error}
            onRefresh={refresh}
            onModeChange={updateMode}
            onSettingsChange={updateSettings}
            onCreateRun={createRun}
            onSelectRun={selectRun}
            onHistoryQueryChange={setHistoryQuery}
            onHistoryStatusChange={setHistoryStatus}
            onLoadMoreHistory={loadMoreHistory}
          />
          {loading ? <div className="rounded-md border border-jarvis-border/60 bg-jarvis-bg/40 px-3 py-2 text-xs text-jarvis-muted">Agent OS wird geladen.</div> : null}
        </div>
        <div className="min-h-[520px] min-w-0 overflow-hidden rounded-lg border border-jarvis-border/70 bg-jarvis-surface/70">
          <ChatView />
        </div>
      </div>
    </div>
  );
}
